import React, { useRef, useState } from 'react';
import { uploadToCloudinary } from '@/lib/cloudinaryConfig';
import { Upload, X, Image as ImageIcon } from 'lucide-react';

const SingleImageUpload = ({ value, onChange, label = 'Upload', placeholder = 'https://...' }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError('');
    try {
      const uploaded = await uploadToCloudinary(file);
      onChange(uploaded);
      setImageUrl('');
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload image.');
    } finally {
      setUploading(false);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addImageUrl = () => {
    if (!imageUrl.trim()) return;
    onChange(imageUrl.trim());
    setImageUrl('');
    setError('');
  };

  const removeImage = () => {
    onChange('');
    setImageUrl('');
    setError('');
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center px-3 py-2 border border-stone-300 rounded-md shadow-sm text-sm font-medium text-stone-700 bg-white hover:bg-stone-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-lime-500 disabled:opacity-50"
        >
          <Upload className="mr-2 h-4 w-4" />
          {uploading ? 'Uploading...' : label}
        </button>
        <input
          type="url"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 border border-stone-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-lime-500"
        />
        <button
          type="button"
          onClick={addImageUrl}
          disabled={!imageUrl.trim()}
          className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-lime-500 hover:bg-lime-400 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-lime-500 disabled:opacity-50"
        >
          Add URL
        </button>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {value ? (
        <div className="relative inline-block rounded-lg border border-stone-200 overflow-hidden">
          <img src={value} alt="Avatar preview" className="h-24 w-24 object-cover" />
          <button
            type="button"
            onClick={removeImage}
            className="absolute -top-2 -right-2 rounded-full bg-red-500 p-1 text-white shadow hover:bg-red-600"
            title="Remove"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-stone-300 p-3 text-sm text-stone-500">
          <ImageIcon className="h-4 w-4" />
          No image selected yet
        </div>
      )}
    </div>
  );
};

export default SingleImageUpload;
